import React from 'react';
import { MARITAL_STATUS, HEIGHTS, COUNTRIES, DIET, BLOODGROUP } from "../../../constants/formData";
import { FAMILY_PLANNING_GOALS, goalLabel } from "../../../constants/familyPlanningGoals";
import { calculateAge } from '../../../utils/helpers';

// ------------------------------------------------------------
// DOB / Age helpers
// ------------------------------------------------------------

const isBlank = (v) => v === null || v === undefined || String(v).trim() === '';

// A DOB counts as "entered" only when day, month and year are all present and
// make sense. Without this check an empty year was treated as 0, so the age
// came out as the current year (e.g. 2026).
function getValidDob(day, month, year) {
  if (isBlank(day) || isBlank(month) || isBlank(year)) return null;

  const d = Number(day);
  const m = Number(month);
  const y = Number(year);
  if (![d, m, y].every(Number.isInteger)) return null;

  const currentYear = new Date().getFullYear();
  if (y < 1900 || y > currentYear) return null;
  if (m < 1 || m > 12) return null;
  if (d < 1 || d > 31) return null;

  return { d, m, y };
}

const keepDigits = (value, maxLength) =>
  String(value).replace(/\D/g, '').slice(0, maxLength);

const BasicInfoSection = ({ isEditing, getValue, onDataChange, editingFields }) => {
  // NEW — same digits-and-one-decimal-point stripping as SignupStep5.jsx's
  // weight field, so typing an invalid character here never reaches state.
  const handleWeightChange = (e) => {
    const cleaned = e.target.value
      .replace(/[^0-9.]/g, "")
      .replace(/(\..*)\./g, "$1");
    onDataChange('weight', cleaned);
  };

  // Age and DOB display: show "--" when there is no (valid) date of birth
  const dob = getValidDob(
    getValue('birth_day'),
    getValue('birth_month'),
    getValue('birth_year')
  );

  let ageDisplay = '--';
  if (dob) {
    const age = calculateAge(dob.y, dob.m, dob.d);
    // keep the original argument order/format the helper already receives
    const safeAge = calculateAge(
      getValue('birth_year'),
      getValue('birth_month'),
      getValue('birth_day')
    );
    const value = Number.isFinite(Number(safeAge)) ? safeAge : age;
    if (value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)) && Number(value) >= 0) {
      ageDisplay = value;
    }
  }

  const dobDisplay = dob
    ? `${getValue('birth_day')}-${getValue('birth_month')}-${getValue('birth_year')}`
    : '--';

  return (
    <div className="row py-3 border-top">
      <div className="col-md-6 pe-md-4">
        <table className="table table-borderless table-sm mini-data mb-0">
          <tbody>
            <tr>
              <td>Age:</td>
              <td>{ageDisplay}</td>
            </tr>
            <tr>
              <td>DOB:</td>
              <td className=''>{isEditing && editingFields === "basic" ? (
                <div className='d-inline-flex gap-1 w-75'>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={2}
                    value={getValue('birth_day') || ''}
                    onChange={(e) => onDataChange('birth_day', keepDigits(e.target.value, 2))}
                    className="form-control form-control-sm "
                    placeholder="Day"
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={2}
                    value={getValue('birth_month') || ''}
                    onChange={(e) => onDataChange('birth_month', keepDigits(e.target.value, 2))}
                    className="form-control form-control-sm "
                    placeholder="Month"
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    value={getValue('birth_year') || ''}
                    onChange={(e) => onDataChange('birth_year', keepDigits(e.target.value, 4))}
                    className="form-control form-control-sm "
                    placeholder="Year"
                  />
                </div>
              ) : (
                dobDisplay
              )}</td>
            </tr>
            <tr>
              <td>Marital Status:</td>
              <td>{isEditing && editingFields === "basic" ? (
                <select
                  value={getValue('marital_status') || ''}
                  onChange={(e) => onDataChange('marital_status', e.target.value)}
                  className="form-select form-select-sm d-inline-block w-75"
                >
                  <option value="">Select</option>
                  {MARITAL_STATUS.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              ) : (
                getValue('marital_status') || '--'
              )}</td>
            </tr>
            <tr>
              <td>Height:</td>
              <td>{isEditing && editingFields === "basic" ? (
                <select
                  value={getValue('height') || ''}
                  onChange={(e) => onDataChange('height', e.target.value)}
                  className="form-select form-select-sm d-inline-block w-75"
                >
                  <option value="">Select</option>
                  {HEIGHTS.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              ) : (
                getValue('height') || '--'
              )}</td>
            </tr>

            {/* NEW — Weight, right under Height. Numeric-only input with a
                "kg" suffix shown on the right while editing. */}
            <tr>
              <td>Weight:</td>
              <td>{isEditing && editingFields === "basic" ? (
                <div style={{ position: "relative", display: "inline-block", width: "75%" }}>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={getValue('weight') || ''}
                    onChange={handleWeightChange}
                    className="form-control form-control-sm"
                    placeholder="e.g. 68"
                    style={{ paddingRight: 34 }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      top: "50%",
                      right: 10,
                      transform: "translateY(-50%)",
                      color: "#888780",
                      fontSize: 12,
                      pointerEvents: "none",
                    }}
                  >
                    kg
                  </span>
                </div>
              ) : (
                getValue('weight') ? `${getValue('weight')} kg` : '--'
              )}</td>
            </tr>

            {/* Family planning goal — the list comes from constants/familyPlanningGoals.js
                so the dashboard and Health page use exactly the same values */}
            <tr>
              <td>Family Planning Goal:</td>
              <td>{isEditing && editingFields === "basic" ? (
                <select
                  value={getValue('family_planning_goal') || ''}
                  onChange={(e) => onDataChange('family_planning_goal', e.target.value)}
                  className="form-select form-select-sm d-inline-block w-75"
                >
                  <option value="">Select</option>
                  {FAMILY_PLANNING_GOALS.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              ) : (
                goalLabel(getValue('family_planning_goal')) || '--'
              )}</td>
            </tr>

          </tbody>
        </table>
      </div>

      <div className="col-md-6 border-start ps-md-4 mt-4 mt-md-0">
        <table className="table table-borderless table-sm mini-data mb-0">
          <tbody>
            <tr>
              <td>Diet:</td>
              <td>{isEditing && editingFields === "basic" ? (
                <select
                  value={getValue('diet') || ''}
                  onChange={(e) => onDataChange('diet', e.target.value)}
                  className="form-select form-select-sm d-inline-block w-75"
                >
                  <option value="">Select</option>
                  {DIET.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              ) : (
                getValue('diet') || '--'
              )}</td>
            </tr>
            <tr>
              <td>Blood Group:</td>
              <td>{isEditing && editingFields === "basic" ? (
                <select
                  value={getValue('blood_group') || ''}
                  onChange={(e) => onDataChange('blood_group', e.target.value)}
                  className="form-select form-select-sm d-inline-block w-75"
                >
                  <option value="">Select</option>
                  {BLOODGROUP.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              ) : (
                getValue('blood_group') || '--'
              )}</td>
            </tr>
            <tr>
              <td>Grow Up:</td>
              <td>{isEditing && editingFields === "basic" ? (
                <select
                  value={getValue('country') || ''}
                  onChange={(e) => onDataChange('country', e.target.value)}
                  className="form-select form-select-sm d-inline-block w-75"
                >
                  <option value="">Select</option>
                  {COUNTRIES.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              ) : (
                getValue('country') || '--'
              )}</td>
            </tr>
            <tr>
              <td>Disability:</td>
              <td>{isEditing && editingFields === "basic" ? (
                <select
                  value={getValue('disability') || ''}
                  onChange={(e) => onDataChange('disability', e.target.value)}
                  className="form-select form-select-sm d-inline-block w-75"
                >
                  {/* Once Yes/No is chosen, "Select" can no longer be picked */}
                  <option value="" disabled={!!getValue('disability')}>Select</option>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                  {/* Keep an older free-text value selectable so it isn't lost */}
                  {getValue('disability') &&
                    !['Yes', 'No'].includes(getValue('disability')) && (
                      <option value={getValue('disability')}>{getValue('disability')}</option>
                    )}
                </select>
              ) : (
                getValue('disability') || '--'
              )}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BasicInfoSection;